<template>
  <div class="range-slider__fallback-field form-group__field">
    <label
      class="range-slider__label visually-hidden"
      :for="id"
      v-text="label"
    />

    <input
      :id="id"
      v-model.number="localModelValue"
      class="range-slider__fallback-input"
      :class="{ [$class('error')]: error }"
      :disabled="disabled"
      :max="config.max"
      :min="config.min"
      :name="name"
      :type="inputType"
      @input="handleInput"
    >

    <p
      v-if="error"
      class="range-slider__fallback-error text-body-s-regular"
      v-text="error"
    />
  </div>
</template>

<script>

/**
 * Vue: Range slider fallback (range-slider-fallback)
 * -----------------------------------------------------------------------------
 * Range slider fallback input.
 * - Uses classes and styles in range slider component.
 *
 * @param {Object} config - Configuration for slider.
 * @param {Boolean} [disabled] - Input is disabled.
 * @param {String} id - ID for input.
 * @param {String} inputType - Input type based on parent state.
 * @param {String} label - Input label text.
 * @param {Number|Object} modelValue - Current value provided by v-model,
 * do not pass an object, only accepts object to avoid prop invalid errors.
 * @param {Number} [otherValue] - Other range slider value for min/max sliders.
 * @param {String} [name] - Input name attribute.
 * @param {String} type - Type of input, expects 'single', 'min', or 'max'.
 *
 * @emits interaction-end - Debounced emit as the user updates the input value.
 * @emits update-value - Debounced emit as the user updates the input value.
 * @emits update:model-value - Emit v-model update event.
 *
 */
import { mapState } from 'vuex'

export default {
  name: 'RangeSliderFallback',

  props: {
    config: {
      type: Object,
      default: () => ({
        max: 100,
        min: 0,
      }),
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    id: {
      type: String,
      default: '',
      required: true,
    },
    inputType: {
      type: String,
      default: 'text',
      required: true,
    },
    label: {
      type: String,
      default: '',
      required: true,
    },
    modelValue: {
      type: [Number, Object],
      default: 0,
      required: true,
    },
    otherValue: {
      type: Number,
      default: 0,
    },
    name: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: 'single',
      required: true,
    },
  },

  emits: {
    'interaction-end': (value) => typeof value === 'number',
    'update-value': (value) => typeof value === 'object',
    'update:model-value': (value) => typeof value === 'number',
  },

  data() {
    return {
      localModelValue: 0,
      error: '',
      timeout: '',
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      throttleSetting: (state) => state.index.settings.throttle,
    }),
  },

  watch: {

    /**
     * Watch v-model value to update local value.
     */
    modelValue() {
      this.localModelValue = this.modelValue
      this.error = ''
    },
  },

  mounted() {
    this.localModelValue = this.modelValue
  },

  methods: {

    /**
     * Handle input.
     * - Debounce input so error message doesn't appear whilst user is typing.
     * - Validate entered input before updating local model value.
     * @param {Object} event - Input event.
     */
    async handleInput(event) {
      if (this.timeout) {
        clearTimeout(this.timeout)
      }

      await new Promise((resolve) => {
        this.timeout = setTimeout(resolve, this.throttleSetting * 2)
      })

      const newValue = event.target.value === '' || event.target.value.includes('e')
        ? false
        : Number(event.target.value)

      /**
       * Ignore empty inputs or inputs with non-numbers.
       */
      if (newValue === false) {
        return
      }

      /**
       * Validate inputs.
       */
      if (this.type === 'min' && newValue >= this.otherValue) {
        this.error = this.$string(
          'general.range_slider.errors.starting',
          { replace: { value: this.otherValue } },
        )

        return
      }

      if (this.type === 'max' && newValue <= this.otherValue) {
        this.error = this.$string(
          'general.range_slider.errors.ending',
          { replace: { value: this.otherValue } },
        )

        return
      }

      if (newValue < this.config.min) {
        this.error = this.$string(
          'general.range_slider.errors.minimum',
          { replace: { value: this.config.min } },
        )

        return
      }

      if (newValue > this.config.max) {
        this.error = this.$string(
          'general.range_slider.errors.maximum',
          { replace: { value: this.config.max } },
        )

        return
      }

      if (newValue % this.config.step) {
        const suggestion = newValue - (newValue % this.config.step)

        this.error = this.$string(
          'general.range_slider.errors.step',
          {
            replace: {
              step: this.config.step,
              suggestion,
            },
          },
        )

        return
      }

      this.error = ''

      /**
       * All events emitted at the same time.
       * - Emitting interaction end on de-focus would leave user waiting for
       *   query to update but it wouldn't happen.
       */
      this.$emit('update:model-value', this.localModelValue)
      this.$emit('interaction-end', this.localModelValue)

      this.$emit(
        'update-value',
        {
          source: 'fallback',
          type: this.type,
          value: this.localModelValue,
        },
      )
    },
  },
}
</script>
